import { RootState } from '../../../../store'
import { formatGreeks, greekColumns } from './DynamicColumnRender'
import { formatLargeNumber } from '../../../../util/screener'
import { green, red } from '../../../../assets/colors'
import { calculateOptionOutlook } from '../../../../util/options'
import { Outlook, outlookToJSON } from '../../../../proto/generated/candle-data-depot-option-chain'
import { OutlookToName } from '../../../typings/options.typings'
import { LiveMarketTextSelector } from '../../../../components/common/LiveMarketText'

const displaySelectorCache = new Map<string, LiveMarketTextSelector>()
const colorSelectorCache = new Map<string, LiveMarketTextSelector>()

export const clearOptionChainLiveDisplayCache = () => {
    displaySelectorCache.clear()
    colorSelectorCache.clear()
}

const formatSignedLtpChange = (value: number): string => {
    if (value === 0) return '0.00'
    const formatted = Math.abs(value).toFixed(2)
    return value > 0 ? `+${formatted}` : `-${formatted}`
}

const getCachedSelector = (
    cache: Map<string, LiveMarketTextSelector>,
    cacheKey: string,
    factory: () => LiveMarketTextSelector
): LiveMarketTextSelector => {
    if (!cache.has(cacheKey)) {
        cache.set(cacheKey, factory())
    }
    return cache.get(cacheKey)!
}

export const getLtpDisplaySelector = (secKey: string, initialLtp: number, priceMultiplier: number): LiveMarketTextSelector =>
    getCachedSelector(displaySelectorCache, `ltp:${secKey}:${initialLtp}:${priceMultiplier}`, () => {
        let seeded = false
        return (state) => {
            const live = state.securityStore.candleChangeBook?.[secKey]?.ltp
            const val =
                live !== undefined
                    ? live / priceMultiplier
                    : !seeded
                      ? Number(initialLtp) / priceMultiplier
                      : 0
            if (live !== undefined) seeded = true
            return val === 0 ? '-' : val.toFixed(2)
        }
    })

export const getLtpChangeDisplaySelector = (
    secKey: string,
    initialLtpChange: number,
    priceMultiplier: number
): LiveMarketTextSelector =>
    getCachedSelector(displaySelectorCache, `ltpChg:${secKey}:${initialLtpChange}:${priceMultiplier}`, () => {
        let seeded = false
        return (state) => {
            const live = state.securityStore.candleChangeBook?.[secKey]?.ltpChange
            const val =
                live !== undefined
                    ? live / priceMultiplier
                    : !seeded
                      ? Number(initialLtpChange) / priceMultiplier
                      : 0
            if (live !== undefined) seeded = true
            return formatSignedLtpChange(val)
        }
    })

export const getLtpChangeColorSelector = (
    secKey: string,
    initialLtpChange: number,
    priceMultiplier: number
): LiveMarketTextSelector =>
    getCachedSelector(colorSelectorCache, `ltpChgColor:${secKey}:${initialLtpChange}:${priceMultiplier}`, () => {
        let seeded = false
        return (state) => {
            const live = state.securityStore.candleChangeBook?.[secKey]?.ltpChange
            const val =
                live !== undefined
                    ? live / priceMultiplier
                    : !seeded
                      ? Number(initialLtpChange) / priceMultiplier
                      : 0
            if (live !== undefined) seeded = true
            return val > 0 ? green : val < 0 ? red : 'inherit'
        }
    })

export const getIvDisplaySelector = (secKey: string, initialIv: number): LiveMarketTextSelector =>
    getCachedSelector(displaySelectorCache, `iv:${secKey}:${initialIv}`, () => {
        let seeded = false
        return (state) => {
            const live = state.securityStore.candleChangeBook?.[secKey]?.impliedVolatility
            const val = live !== undefined ? live : !seeded ? Number(initialIv) : 0
            if (live !== undefined) seeded = true
            return Number.isNaN(val) ? '-' : Number(val).toFixed(2)
        }
    })

export const getOiDisplaySelector = (secKey: string, initialOi: number, lotSize: number): LiveMarketTextSelector =>
    getCachedSelector(displaySelectorCache, `oi:${secKey}:${initialOi}:${lotSize}`, () => {
        let seeded = false
        return (state) => {
            const live = state.securityStore.candleChangeBook?.[secKey]?.openInterest
            const val = live !== undefined ? live : !seeded ? Number(initialOi) : 0
            if (live !== undefined) seeded = true
            if (!val) return '-'
            const quantityInLots = state.commonStore.showOptionChainQuantityInLots
            return formatLargeNumber(quantityInLots ? val / lotSize : val)
        }
    })

const formatSignedOiChange = (value: number, lotSize: number, quantityInLots: boolean): string => {
    if (value === 0) return '-'
    const adjusted = quantityInLots ? value / lotSize : value
    const formatted = formatLargeNumber(Math.abs(adjusted))
    return adjusted > 0 ? `+${formatted}` : `-${formatted}`
}

export const getOiChangeDisplaySelector = (secKey: string, initialOiChange: number, lotSize: number): LiveMarketTextSelector =>
    getCachedSelector(displaySelectorCache, `oiChg:${secKey}:${initialOiChange}:${lotSize}`, () => {
        let seeded = false
        return (state) => {
            const live = state.securityStore.candleChangeBook?.[secKey]?.oiChange
            const val = live !== undefined ? live : !seeded ? Number(initialOiChange) : 0
            if (live !== undefined) seeded = true
            const quantityInLots = state.commonStore.showOptionChainQuantityInLots
            return formatSignedOiChange(val, lotSize, quantityInLots)
        }
    })

export const getOiChangeColorSelector = (secKey: string, initialOiChange: number): LiveMarketTextSelector =>
    getCachedSelector(colorSelectorCache, `oiChgColor:${secKey}:${initialOiChange}`, () => {
        let seeded = false
        return (state) => {
            const live = state.securityStore.candleChangeBook?.[secKey]?.oiChange
            const val = live !== undefined ? live : !seeded ? Number(initialOiChange) : 0
            if (live !== undefined) seeded = true
            return val > 0 ? green : val < 0 ? red : 'inherit'
        }
    })

export const getBidDisplaySelector = (secKey: string, initialBid: number, priceMultiplier: number): LiveMarketTextSelector =>
    getCachedSelector(displaySelectorCache, `bid:${secKey}:${initialBid}:${priceMultiplier}`, () => {
        let seeded = false
        return (state) => {
            const live = state.securityStore.candleChangeBook?.[secKey]?.topBid
            const val = live !== undefined ? live : !seeded ? initialBid : 0
            if (live !== undefined) seeded = true
            return !val ? '-' : (val / priceMultiplier).toFixed(2)
        }
    })

export const getAskDisplaySelector = (secKey: string, initialAsk: number, priceMultiplier: number): LiveMarketTextSelector =>
    getCachedSelector(displaySelectorCache, `ask:${secKey}:${initialAsk}:${priceMultiplier}`, () => {
        let seeded = false
        return (state) => {
            const live = state.securityStore.candleChangeBook?.[secKey]?.topAsk
            const val = live !== undefined ? live : !seeded ? initialAsk : 0
            if (live !== undefined) seeded = true
            return !val ? '-' : (val / priceMultiplier).toFixed(2)
        }
    })

export const getFieldDisplaySelector = (
    secKey: string,
    field: string,
    initialValue: number | string,
    options?: { lotSize?: number; formatLarge?: boolean }
): LiveMarketTextSelector =>
    getCachedSelector(
        displaySelectorCache,
        `field:${secKey}:${field}:${initialValue}:${options?.lotSize}:${options?.formatLarge}`,
        () => {
            let seeded = false
            return (state) => {
                const row = state.securityStore.candleChangeBook?.[secKey]
                let returnValue: number | string | undefined
                if (greekColumns.includes(field)) {
                    const greek = row?.optionGreeks?.[field as keyof typeof row.optionGreeks]
                    if (greek !== undefined && greek !== null && !Number.isNaN(greek)) {
                        returnValue = field === 'gamma' ? greek.toFixed(4) : formatGreeks(greek)
                    } else if (!seeded && initialValue !== undefined) {
                        returnValue =
                            field === 'gamma'
                                ? Number(initialValue).toFixed(4)
                                : formatGreeks(Number(initialValue))
                    } else {
                        returnValue = '-'
                    }
                } else {
                    const live = row?.[field as keyof typeof row] as number | undefined
                    returnValue = live !== undefined ? live : !seeded ? initialValue : '-'
                    if (live !== undefined) seeded = true
                }
                if (greekColumns.includes(field) && row?.optionGreeks?.[field as keyof typeof row.optionGreeks] !== undefined) {
                    seeded = true
                }
                if (field === 'volume' && returnValue !== '-') {
                    const quantityInLots = state.commonStore.showOptionChainQuantityInLots
                    if (quantityInLots) {
                        returnValue = Number(returnValue) / (options?.lotSize ?? 50)
                    }
                }
                if (options?.formatLarge && returnValue !== '-' && returnValue !== undefined) {
                    returnValue = formatLargeNumber(Number(returnValue))
                }
                if (Number(returnValue) === 0) return '-'
                return String(returnValue ?? '-')
            }
        }
    )

export const getOutlookDisplaySelector = (
    secKey: string,
    initialOiChange: number,
    initialLtpChangePercent: number
): LiveMarketTextSelector =>
    getCachedSelector(
        displaySelectorCache,
        `outlook:${secKey}:${initialOiChange}:${initialLtpChangePercent}`,
        () => {
            let oiSeeded = false
            let ltpSeeded = false
            return (state) => {
                const row = state.securityStore.candleChangeBook?.[secKey]
                let oiChange = row?.oiChange
                if (oiChange === undefined && !oiSeeded) {
                    oiChange = Number(initialOiChange)
                } else if (oiChange !== undefined) {
                    oiSeeded = true
                }
                let ltpChangePercent = row?.ltpChangePercent
                if (ltpChangePercent === undefined && !ltpSeeded) {
                    ltpChangePercent = Number(initialLtpChangePercent)
                } else if (ltpChangePercent !== undefined) {
                    ltpSeeded = true
                }
                const outlook = calculateOptionOutlook(Number(ltpChangePercent ?? 0), Number(oiChange ?? 0))
                return [Outlook.OUTLOOK_UNKNOWN, Outlook.UNRECOGNIZED, undefined].includes(outlook)
                    ? '-'
                    : OutlookToName[outlookToJSON(Number(outlook))]
            }
        }
    )
